import {
  menu_category,
  menu_product,
  modifier_group,
  modifier_option,
  type PowerSyncDrizzleDB,
  product_modifier_group,
} from "@comandero/powersync";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { generateId } from "@fleet/utils";

/** Creates a menu category. Returns the id.*/
export async function createCategoryMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  opts?: { color?: string; icon?: string; sortOrder?: number },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(menu_category).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    color: opts?.color ?? null,
    icon: opts?.icon ?? null,
    sort_order: opts?.sortOrder ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateCategoryMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ name: string; color: string | null; icon: string | null; sort_order: number }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, menu_category, menu_category.id, id, { ...updates }, options);
}

export async function deleteCategoryMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    menu_category,
    menu_category.id,
    id,
    { deleted_at: nowISO() },
    options,
  );
}

/** Crea un producto. `price`/`taxRate` en centavos / basis points.*/
export async function createProductMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  price: number,
  opts?: {
    categoryId?: string | null;
    sku?: string;
    taxRate?: number;
    prepStation?: string;
    kind?: "food" | "drink";
    isAvailable?: boolean;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(menu_product).values({
    id,
    organization_id: organizationId,
    category_id: opts?.categoryId ?? null,
    name: name.trim(),
    price: Math.trunc(price),
    sku: opts?.sku ?? null,
    is_available: opts?.isAvailable === false ? 0 : 1,
    tax_rate: opts?.taxRate ?? 0,
    prep_station: opts?.prepStation ?? null,
    kind: opts?.kind ?? "food",
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateProductMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    price: number;
    category_id: string | null;
    sku: string | null;
    is_available: number;
    tax_rate: number;
    prep_station: string | null;
    kind: string;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, menu_product, menu_product.id, id, { ...updates }, options);
}

export async function deleteProductMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, menu_product, menu_product.id, id, { deleted_at: nowISO() }, options);
}

/** Crea un grupo de modificadores.*/
export async function createModifierGroupMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  opts?: { minSelect?: number; maxSelect?: number; isRequired?: boolean },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(modifier_group).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    min_select: opts?.minSelect ?? 0,
    max_select: opts?.maxSelect ?? 1,
    is_required: opts?.isRequired ? 1 : 0,
    sort_order: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function createModifierOptionMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  modifierGroupId: string,
  name: string,
  priceDelta = 0,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(modifier_option).values({
    id,
    organization_id: organizationId,
    modifier_group_id: modifierGroupId,
    name: name.trim(),
    price_delta: Math.trunc(priceDelta),
    sort_order: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Asocia un grupo de modificadores a un producto (N:M).*/
export async function linkProductModifierGroupMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  productId: string,
  modifierGroupId: string,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(product_modifier_group).values({
    id,
    organization_id: organizationId,
    product_id: productId,
    modifier_group_id: modifierGroupId,
    created_at: now,
    updated_at: now,
  });
  return id;
}
